import React, { useMemo, useState } from "react";

import { useMutation } from "@apollo/client";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { isSameMonth, isSameWeek } from "date-fns";
import { useNavigate } from "react-router-dom";

import { TextButton } from "@/components/Button";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { AppointmentStatusKind, UpdateAppointmentInput } from "@/gql/graphql";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import useGlobalPictureInPicture from "@/hooks/usePictureInPicture";
import AppointmentCard from "@/pages/Appointments/components/AppointmentCard";
import AppointmentConfirmationModal from "@/pages/Appointments/components/AppointmentConfirmationModal";
import CancelAppointmentModal from "@/pages/Appointments/components/CancelAppointmentModal";
import EditAppointment from "@/pages/Appointments/components/EditAppointment";
import { ConfirmationDetails } from "@/pages/Appointments/types";
import { isAppointmentFinished } from "@/pages/Appointments/utils/isAppointmentFinished";
import { colors } from "@/theme/overrides/colors";
import { format, formatISODate, parseDateTime } from "@/utils/datetime";

import { ClientsSummaryCard } from "./ClientsSummaryCard";
import { clientAppointmentsFragment } from "../graphql/dashboardPageFragments";

const updateAppointmentMutation = graphql(`
  mutation UpdateAppointment2($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      appointment {
        id
        status
        startTime
        startDate
        endTime
        client {
          id
          profile {
            id
            fullName
          }
        }
      }
    }
  }
`);

interface SidePanelProps {
  loading?: boolean;
}

const SidePanel: React.FC<SidePanelProps> = ({ loading = false }) => {
  const navigate = useNavigate();
  const pip = useGlobalPictureInPicture();
  const [appointmentId, setAppointmentId] = useState<string>("");
  const [editVisibility, setEditVisibility] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAppointment] = useMutation(updateAppointmentMutation);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationDetails, setConfirmationDetails] =
    useState<ConfirmationDetails>();
  const [updateAppointment] = useMutation(updateAppointmentMutation);

  const snackbar = useGlobalSnackbar();

  // API
  const [clientsData] = useFragment(clientAppointmentsFragment, "ROOT_QUERY", {
    variables: { currentDate: formatISODate(new Date()) },
  });

  const upcomingAppointments = useMemo(() => {
    if (clientsData?.appointments.length === 0) {
      return [];
    }

    return clientsData?.appointments[0].appointments
      ?.filter((appointment) => appointment.status === "upcoming")
      .sort((a, b) => {
        const startTimeA = new Date(`${a.startDate} ${a.startTime}`).getTime();
        const startTimeB = new Date(`${b.startDate} ${b.startTime}`).getTime();

        return startTimeA - startTimeB;
      })
      .slice(0, 2);
  }, [clientsData]);

  const clientsCount = useMemo(
    () => clientsData?.clients.nodes.length,
    [clientsData?.clients]
  );
  const clientAvatars = useMemo(
    () =>
      clientsData?.clients.nodes
        .map((client) => client.profile?.avatarUrl)
        .slice(0, 4),
    [clientsData?.clients]
  );
  const clientsWeeklyCount = useMemo(
    () =>
      clientsData?.clients.nodes.filter((client) =>
        isSameWeek(parseDateTime(client.createdAt), new Date())
      ).length,
    [clientsData?.clients]
  );
  const clientsMonthlyCount = useMemo(
    () =>
      clientsData?.clients.nodes.filter((client) =>
        isSameMonth(parseDateTime(client.createdAt), new Date())
      ).length,
    [clientsData?.clients]
  );
  const pendingInvitesCount = useMemo(
    () =>
      clientsData?.clients.nodes.filter(
        (client) => client.invitedAt && !client.accountId
      ).length,
    [clientsData?.clients]
  );
  const pendingClientAvatars = useMemo(
    () =>
      clientsData?.clients.nodes
        .filter((client) => client.invitedAt && !client.accountId)
        .map((client) => client.profile?.avatarUrl)
        .slice(0, 4),
    [clientsData?.clients]
  );

  // Functions
  async function handleEditAppointment(input: UpdateAppointmentInput) {
    try {
      await updateAppointment({
        variables: { input: input },
      });
      setEditVisibility(false);
      snackbar.open("Successfully saved appointment details.");
    } catch (error) {
      snackbar.open(`There was an error in updating your appointment ${error}`);
    }
  }

  async function handleCancelAppointment() {
    const input: UpdateAppointmentInput = {
      id: appointmentId,
      patch: {
        status: AppointmentStatusKind.Cancelled,
      },
    };
    try {
      const result = await cancelAppointment({
        variables: { input },
      });

      setShowCancelModal(false);
      setShowConfirmation(true);
      setConfirmationDetails({
        startDate: format(
          {
            date:
              result.data?.updateAppointment?.appointment?.startDate ??
              new Date(),
          },
          "dd MMMM yyyy"
        ),
        startTime: format(
          {
            timeofday:
              result.data?.updateAppointment?.appointment?.startTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        endTime: format(
          {
            timeofday:
              result.data?.updateAppointment?.appointment?.endTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        fullName:
          result.data?.updateAppointment?.appointment?.client.profile?.fullName,

        status: result.data?.updateAppointment?.appointment?.status,
      });
    } catch (error) {
      snackbar.open(
        `There was an error in cancelling your appointment ${error}`
      );
    }
  }

  function handleMenuClick(type: string, id: string) {
    setAppointmentId(id);
    switch (type) {
      case "edit": {
        setEditVisibility(true);
        break;
      }
      case "cancel": {
        setShowCancelModal(true);
      }
    }
  }

  return (
    <Box padding="16px" height="100%">
      <ClientsSummaryCard
        isLoading={loading}
        title="My Clients"
        avatars={clientAvatars}
        clientsCount={clientsCount ?? 0}
        weeklyCount={clientsWeeklyCount}
        monthlyCount={clientsMonthlyCount}
      />
      <Box height="16px" />
      <ClientsSummaryCard
        isLoading={loading}
        title="Pending Invites"
        avatars={pendingClientAvatars}
        clientsCount={pendingInvitesCount ?? 0}
        onClick={() => navigate("/clients/invites")}
      />
      <Box height="16px" />
      {Features.appointmentsEnabled && (
        <Box>
          {upcomingAppointments?.map((appointment) => {
            if (
              appointment.status === "cancelled" ||
              isAppointmentFinished(appointment)
            ) {
              return null;
            }
            return (
              <AppointmentCard
                id={appointment.id}
                onEdit={() => {
                  setAppointmentId(appointment.id);
                  setEditVisibility(true);
                }}
                key={appointment.id}
                onClickMenu={handleMenuClick}
                onJoin={() =>
                  pip.open(appointment.meetingDetails?.roomUrl ?? "")
                }
              />
            );
          })}
          <TextButton
            text="See All Appointments"
            endIcon={
              <ChevronRightIcon htmlColor={colors.mapped.surface.default} />
            }
            onClick={() => navigate("/appointments")}
          />
          <Box height="32px" />
        </Box>
      )}
      <EditAppointment
        id={appointmentId}
        open={editVisibility}
        onClose={() => setEditVisibility(false)}
        handleEdit={handleEditAppointment}
        onCancel={() => setShowCancelModal(true)}
      />
      <CancelAppointmentModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        handleCancel={handleCancelAppointment}
      />
      <AppointmentConfirmationModal
        startDate={confirmationDetails?.startDate}
        startTime={confirmationDetails?.startTime}
        endTime={confirmationDetails?.endTime}
        fullName={confirmationDetails?.fullName}
        open={showConfirmation}
        status={confirmationDetails?.status}
        onClose={() => {
          setShowConfirmation(false);
        }}
      />
    </Box>
  );
};

export default SidePanel;
